import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, QrCode, MessageCircle, Loader2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { formatINR, waLink, buildOrderMessage } from "@/config";

const FIELDS = [
  ["name", "Full Name", "text", "Ravi Kumar"],
  ["mobile", "Mobile Number", "tel", "9876543210"],
  ["email", "Email", "email", "you@email.com"],
  ["address", "Complete Address", "text", "House no, street, area"],
  ["city", "City", "text", "Jammu"],
  ["state", "State", "text", "Jammu & Kashmir"],
  ["pincode", "Pincode", "text", "180001"],
];

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, subtotal, discount, shipping, total, coupon, setCoupon, clearCart } = useStore();
  const [form, setForm] = useState({ name: "", mobile: "", email: "", address: "", city: "", state: "", pincode: "" });
  const [couponCode, setCouponCode] = useState(coupon?.code || "");
  const [applying, setApplying] = useState(false);
  const [method, setMethod] = useState("qr");
  const [placing, setPlacing] = useState(false);

  if (cart.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-[#1F130B]">Your cart is empty</h1>
        <button onClick={() => navigate("/shop")} className="mt-5 rounded-full bg-[#C2410C] text-white px-6 py-3 font-semibold">Continue Shopping</button>
      </div>
    );
  }

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.get("/coupons/validate", { params: { code: couponCode, subtotal } });
      setCoupon(data);
      toast.success(`Coupon applied — ${data.label}`);
    } catch (e) {
      setCoupon(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    } finally {
      setApplying(false);
    }
  };

  const validate = () => {
    for (const [k, label] of FIELDS) {
      if (!form[k].trim()) { toast.error(`Please enter ${label}`); return false; }
    }
    if (!/^\d{10}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit mobile number"); return false; }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("Enter a valid 6-digit pincode"); return false; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) { toast.error("Enter a valid email"); return false; }
    return true;
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      const items = cart.map((i) => ({ slug: i.slug, weight_label: i.weight_label, qty: i.qty }));
      const { data: order } = await api.post("/orders", {
        customer: form,
        items,
        coupon_code: coupon?.code || null,
        payment_method: method,
      });

      if (method === "whatsapp") {
        const msg = buildOrderMessage({ customer: form, items: cart, subtotal, discount, shipping, total });
        window.open(waLink(msg), "_blank");
      }
      clearCart();
      navigate(`/order/${order.order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order. Please try again.");
      setPlacing(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F130B] mb-6">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        {/* form + payment */}
        <div className="space-y-6">
          <div className="rounded-2xl bg-white border border-[#E7E0D6] p-6">
            <h2 className="font-display text-xl font-bold text-[#1F130B] mb-4">Delivery Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map(([k, label, type, ph]) => (
                <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">{label}</label>
                  <input
                    type={type}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    placeholder={ph}
                    data-testid={`checkout-${k}`}
                    className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C]"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-[#E7E0D6] p-6">
            <h2 className="font-display text-xl font-bold text-[#1F130B] mb-4">Payment Method</h2>
            <div className="space-y-3">
              {[
                { key: "qr", icon: QrCode, title: "UPI QR Payment", sub: "Scan our UPI/BHIM QR, pay, then confirm your order" },
                { key: "whatsapp", icon: MessageCircle, title: "Order via WhatsApp", sub: "Send order to us & pay as guided" },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMethod(m.key)}
                  data-testid={`payment-method-${m.key}`}
                  className={`w-full flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-colors ${
                    method === m.key ? "border-[#C2410C] bg-[#FEF3C7]/60" : "border-[#E7E0D6] hover:border-[#D97706]"
                  }`}
                >
                  <div className={`h-11 w-11 rounded-full grid place-items-center ${method === m.key ? "bg-[#C2410C] text-white" : "bg-[#F3ECE2] text-[#9A3412]"}`}>
                    <m.icon size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-[#1F130B]">{m.title}</p>
                    <p className="text-xs text-[#6B5B52]">{m.sub}</p>
                  </div>
                  <div className={`h-5 w-5 rounded-full border-2 ${method === m.key ? "border-[#C2410C] bg-[#C2410C]" : "border-[#D6C9BA]"}`} />
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-[#6B5B52] flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#15803D]" /> 100% secure. This is a prepaid store — no cash on delivery.</p>
          </div>
        </div>

        {/* summary */}
        <div>
          <div className="rounded-2xl bg-white border border-[#E7E0D6] p-6 sticky top-28">
            <h2 className="font-display text-xl font-bold text-[#1F130B] mb-4">Order Summary</h2>
            <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
              {cart.map((it) => (
                <div key={it.key} className="flex gap-3">
                  <img src={it.image} alt={it.name} className="h-14 w-14 rounded-lg object-cover border border-[#E7E0D6]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1F130B] truncate">{it.name}</p>
                    <p className="text-xs text-[#6B5B52]">{it.weight_label} × {it.qty}</p>
                  </div>
                  <p className="text-sm font-semibold text-[#1F130B]">{formatINR(it.price * it.qty)}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-dashed border-[#E7E0D6]">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                    data-testid="coupon-input"
                    className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#C2410C]"
                  />
                </div>
                <button onClick={applyCoupon} disabled={applying} data-testid="coupon-apply-btn" className="rounded-xl bg-[#1C120C] text-white px-4 text-sm font-semibold disabled:opacity-60">{applying ? "..." : "Apply"}</button>
              </div>
              {coupon && (
                <div className="mt-2 flex items-center justify-between rounded-lg bg-[#EAF6EC] px-3 py-2">
                  <span className="text-xs font-semibold text-[#15803D]">{coupon.code} applied</span>
                  <button onClick={() => { setCoupon(null); setCouponCode(""); }} data-testid="coupon-remove-btn" className="text-[#15803D]"><X size={14} /></button>
                </div>
              )}
              <p className="mt-2 text-[11px] text-[#9CA3AF]">Try: WELCOME10, KASHMIR15, FESTIVE20, FLAT100</p>
            </div>

            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-[#6B5B52]"><span>Subtotal</span><span className="font-semibold text-[#1F130B]">{formatINR(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#15803D]"><span>Discount</span><span className="font-semibold">-{formatINR(discount)}</span></div>}
              <div className="flex justify-between text-[#6B5B52]"><span>Shipping</span><span className="font-semibold">{shipping ? formatINR(shipping) : "FREE"}</span></div>
              <div className="flex justify-between text-lg font-bold text-[#1F130B] pt-2 border-t border-dashed border-[#E7E0D6]"><span>Total</span><span data-testid="checkout-total">{formatINR(total)}</span></div>
            </div>

            <button
              onClick={placeOrder}
              disabled={placing}
              data-testid="place-order-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold py-3.5 transition-colors disabled:opacity-70"
            >
              {placing ? <><Loader2 size={18} className="animate-spin" /> Processing...</> : method === "whatsapp" ? "Place Order via WhatsApp" : `Place Order · ${formatINR(total)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
